import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { EVENT_PLACEHOLDER_IMAGE, resolveEventCoverUrl } from "@/lib/event-cover";

interface EventImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  /** true para o primeiro card visível da página (LCP) — desativa lazy e prioriza fetch */
  priority?: boolean;
}

/**
 * Hash simples para gerar seed determinístico baseado na URL
 * Evita sempre a mesma imagem Unsplash e bypass de rate limiting
 */
function hashUrl(url: string): string {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

export function EventImage({ src, alt, className, priority = false }: EventImageProps) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  const finalSrc = useMemo(() => {
    const imgSrc = hasError ? null : src;
    const resolved = resolveEventCoverUrl(imgSrc);

    try {
      const low = resolved.toLowerCase();
      if (low.includes("netflix")) {
        const seed = hashUrl(resolved);
        return `https://source.unsplash.com/1200x750/?event,concert&sig=${seed}`;
      }
    } catch {
      // ignore
    }

    return resolved;
  }, [src, hasError]);

  return (
    <img
      src={finalSrc}
      alt={alt}
      className={cn("w-full h-full object-cover", className)}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      onError={() => {
        if (finalSrc !== EVENT_PLACEHOLDER_IMAGE) setHasError(true);
      }}
    />
  );
}
